// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * All DOM construction and the chrome around the instrument: the layout, the
 * mode tabs, the three modals, the photo drop zone, the glossary tooltip and the
 * toast. main.ts owns the state machine and drives the numbers into the handles
 * this returns.
 */

import { GLOSSARY } from './glossary';

export interface AppRefs {
  // Mode
  tabs: HTMLElement;
  instrumentSection: HTMLElement;
  photoSection: HTMLElement;
  capturesSection: HTMLElement;
  // Readout
  readoutMain: HTMLElement;
  readoutStatus: HTMLElement;
  readoutSub: HTMLElement;
  liveTag: HTMLElement;
  gauge: HTMLCanvasElement;
  // Conversions
  convGrade: HTMLElement;
  convPitch: HTMLElement;
  convRatio: HTMLElement;
  rampVerdict: HTMLElement;
  // Controls
  startBtn: HTMLButtonElement;
  calBtn: HTMLButtonElement;
  resetCalBtn: HTMLButtonElement;
  captureBtn: HTMLButtonElement;
  stopBtn: HTMLButtonElement;
  controlsNote: HTMLElement;
  // Photo
  dropZone: HTMLElement;
  fileInput: HTMLInputElement;
  sampleBtn: HTMLButtonElement;
  photoCanvas: HTMLCanvasElement;
  photoStage: HTMLElement;
  photoUndoBtn: HTMLButtonElement;
  photoClearBtn: HTMLButtonElement;
  photoCaptureBtn: HTMLButtonElement;
  photoPngBtn: HTMLButtonElement;
  photoPdfBtn: HTMLButtonElement;
  photoShareBtn: HTMLButtonElement;
  photoReadout: HTMLElement;
  photoHint: HTMLElement;
  // Captures
  captureList: HTMLElement;
  capturesEmpty: HTMLElement;
  dlCsvBtn: HTMLButtonElement;
  dlPngBtn: HTMLButtonElement;
  copyPngBtn: HTMLButtonElement;
  sharePngBtn: HTMLButtonElement;
  copySummaryBtn: HTMLButtonElement;
  clearCapsBtn: HTMLButtonElement;
  // Drawer / banner
  drawer: HTMLElement;
  drawerBody: HTMLElement;
  drawerToggle: HTMLButtonElement;
  banner: HTMLElement;
}

const ICON = {
  level:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="9" width="20" height="6" rx="2"/><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none"/><path d="M9 12h.01M15 12h.01"/></svg>',
  play: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
  stop: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>',
  capture:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>',
  cal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20v-6M12 8V4M6 12H4m16 0h-2"/><circle cx="12" cy="12" r="3"/></svg>',
  image:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-4.35-4.35a2 2 0 0 0-2.83 0L4 21"/></svg>',
};

export function renderApp(root: HTMLElement): AppRefs {
  root.innerHTML = `
    <header class="topbar">
      <div class="brand">
        <span class="brand-mark">${ICON.level}</span>
        <div class="brand-text">
          <span class="brand-name">Tiltwell</span>
          <span class="brand-tag">level &amp; angle finder</span>
        </div>
      </div>
      <nav class="topnav">
        <button type="button" class="nav-btn" data-modal="how">How it works</button>
        <button type="button" class="nav-btn" data-modal="privacy">Privacy</button>
        <button type="button" class="nav-btn" data-modal="about">About</button>
        <button type="button" class="nav-btn drawer-toggle" id="drawer-toggle" aria-expanded="false">Event log</button>
      </nav>
    </header>

    <button type="button" class="trust-banner" id="trust-banner">
      Runs entirely in your browser. The orientation sensor stream never leaves your device.
    </button>

    <main class="main-content">
      <div class="tabs" id="tabs" role="tablist" aria-label="Measurement mode">
        <button type="button" class="tab" data-mode="level" role="tab" aria-selected="true">Level</button>
        <button type="button" class="tab" data-mode="angle" role="tab" aria-selected="false">Angle</button>
        <button type="button" class="tab" data-mode="photo" role="tab" aria-selected="false">Photo</button>
      </div>

      <section class="instrument" id="instrument-section">
        <div class="readout">
          <div class="readout-main-row">
            <span class="readout-main" id="readout-main">0.0°</span>
            <span class="live-tag" id="live-tag" hidden>live</span>
          </div>
          <div class="readout-status" id="readout-status" data-tone="idle">press start</div>
          <div class="readout-sub" id="readout-sub"></div>
        </div>

        <div class="gauge-wrap">
          <canvas id="gauge" aria-label="Live level and angle gauge"></canvas>
        </div>

        <div class="conv-strip">
          <div class="conv-tile"><span class="conv-label" data-term="grade">grade</span><span class="conv-val" id="conv-grade">—</span></div>
          <div class="conv-tile"><span class="conv-label" data-term="roof pitch">roof pitch</span><span class="conv-val" id="conv-pitch">—</span></div>
          <div class="conv-tile"><span class="conv-label" data-term="ratio">ratio</span><span class="conv-val" id="conv-ratio">—</span></div>
        </div>
        <p class="ramp-verdict" id="ramp-verdict"></p>

        <div class="controls">
          <button type="button" class="btn btn-primary" id="start-btn">${ICON.play}<span>Start measuring</span></button>
          <button type="button" class="btn btn-ghost" id="cal-btn" hidden>${ICON.cal}<span>Calibrate</span></button>
          <button type="button" class="btn btn-record" id="capture-btn" hidden>${ICON.capture}<span>Capture</span></button>
          <button type="button" class="btn btn-ghost" id="stop-btn" hidden>${ICON.stop}<span>Stop</span></button>
          <button type="button" class="btn btn-small btn-quiet" id="reset-cal-btn" hidden>Reset zero</button>
        </div>
        <p class="controls-note" id="controls-note">
          Lay the phone flat on a surface to check it is <span data-term="level">level</span>, or stand it
          upright against a wall or door frame to check it is plumb. Use <strong>Calibrate</strong> to zero
          it against whatever you are checking. Switch to <strong>Angle</strong> to measure a slope or
          <span data-term="roof pitch">roof pitch</span>.
        </p>
      </section>

      <section class="photo-section" id="photo-section" hidden>
        <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="Upload a photo to measure an angle in">
          <div class="dz-icon">${ICON.image}</div>
          <div class="dz-text">
            <strong>Measure an angle in a photo</strong>
            <span>No sensor on this device? Drop a photo, then tap points to read the angle.</span>
            <button type="button" class="sample-link" id="sample-btn">${ICON.image}<span>Try a sample photo</span></button>
          </div>
          <input type="file" id="file-input" accept="image/*" hidden />
        </div>

        <div class="photo-stage" id="photo-stage" hidden>
          <canvas id="photo-canvas" aria-label="Photo with angle overlay"></canvas>
        </div>
        <p class="photo-readout" id="photo-readout" hidden></p>
        <p class="photo-hint" id="photo-hint" hidden>
          Tap two points for a slope from horizontal, or three points for the angle at the middle one.
          Drag a point to fine-tune exactly where it sits.
        </p>
        <div class="controls photo-controls" id="photo-controls" hidden>
          <button type="button" class="btn btn-small" id="photo-undo">Undo point</button>
          <button type="button" class="btn btn-small" id="photo-clear">Clear points</button>
          <button type="button" class="btn btn-record btn-small" id="photo-capture">${ICON.capture}<span>Capture angle</span></button>
          <button type="button" class="btn btn-small" id="photo-png">Save annotated PNG</button>
          <button type="button" class="btn btn-small" id="photo-pdf">Save annotated PDF</button>
          <button type="button" class="btn btn-small" id="photo-share" hidden>Share photo</button>
        </div>
      </section>

      <section class="captures" id="captures" hidden>
        <div class="captures-head">
          <h2 class="captures-title">Captured measurements</h2>
          <button type="button" class="filter-pill clear" id="clear-caps">clear all</button>
        </div>
        <p class="captures-empty" id="captures-empty">Nothing captured yet — press Capture to keep a reading.</p>
        <div class="capture-list" id="capture-list"></div>
        <div class="result-actions">
          <button type="button" class="btn btn-small" id="dl-csv">Download CSV</button>
          <button type="button" class="btn btn-small" id="dl-png">Report PNG</button>
          <button type="button" class="btn btn-small" id="copy-png">Copy image</button>
          <button type="button" class="btn btn-small" id="share-png" hidden>Share</button>
          <button type="button" class="btn btn-small" id="copy-summary">Copy summary</button>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="footer-inner">
        Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      </div>
    </footer>

    <aside class="drawer" id="drawer" hidden aria-hidden="true"><div class="drawer-body" id="drawer-body"></div></aside>
    <div class="scrim" id="scrim" hidden></div>
    <div class="modal-host" id="modal-host" hidden></div>
    <div class="glossary-tip" id="glossary-tip" hidden></div>
    <div class="toast" id="toast" hidden></div>
  `;

  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;

  const refs: AppRefs = {
    tabs: $('tabs'),
    instrumentSection: $('instrument-section'),
    photoSection: $('photo-section'),
    capturesSection: $('captures'),
    readoutMain: $('readout-main'),
    readoutStatus: $('readout-status'),
    readoutSub: $('readout-sub'),
    liveTag: $('live-tag'),
    gauge: $<HTMLCanvasElement>('gauge'),
    convGrade: $('conv-grade'),
    convPitch: $('conv-pitch'),
    convRatio: $('conv-ratio'),
    rampVerdict: $('ramp-verdict'),
    startBtn: $<HTMLButtonElement>('start-btn'),
    calBtn: $<HTMLButtonElement>('cal-btn'),
    resetCalBtn: $<HTMLButtonElement>('reset-cal-btn'),
    captureBtn: $<HTMLButtonElement>('capture-btn'),
    stopBtn: $<HTMLButtonElement>('stop-btn'),
    controlsNote: $('controls-note'),
    dropZone: $('dropzone'),
    fileInput: $<HTMLInputElement>('file-input'),
    sampleBtn: $<HTMLButtonElement>('sample-btn'),
    photoCanvas: $<HTMLCanvasElement>('photo-canvas'),
    photoStage: $('photo-stage'),
    photoUndoBtn: $<HTMLButtonElement>('photo-undo'),
    photoClearBtn: $<HTMLButtonElement>('photo-clear'),
    photoCaptureBtn: $<HTMLButtonElement>('photo-capture'),
    photoPngBtn: $<HTMLButtonElement>('photo-png'),
    photoPdfBtn: $<HTMLButtonElement>('photo-pdf'),
    photoShareBtn: $<HTMLButtonElement>('photo-share'),
    photoReadout: $('photo-readout'),
    photoHint: $('photo-hint'),
    captureList: $('capture-list'),
    capturesEmpty: $('captures-empty'),
    dlCsvBtn: $<HTMLButtonElement>('dl-csv'),
    dlPngBtn: $<HTMLButtonElement>('dl-png'),
    copyPngBtn: $<HTMLButtonElement>('copy-png'),
    sharePngBtn: $<HTMLButtonElement>('share-png'),
    copySummaryBtn: $<HTMLButtonElement>('copy-summary'),
    clearCapsBtn: $<HTMLButtonElement>('clear-caps'),
    drawer: $('drawer'),
    drawerBody: $('drawer-body'),
    drawerToggle: $<HTMLButtonElement>('drawer-toggle'),
    banner: $('trust-banner'),
  };

  wireModals(root);
  initGlossary(root);
  return refs;
}

// ── Modals ───────────────────────────────────────────────────────────

const MODALS: Record<string, { title: string; body: string }> = {
  how: {
    title: 'How it works',
    body: `
      <ol class="steps">
        <li><strong>Read the orientation sensor.</strong> When you press start, Tiltwell subscribes to your
          device's <span data-term="orientation sensor">orientation sensor</span> and reads how the phone is
          tilted many times a second.</li>
        <li><strong>Turn tilt into a gravity direction.</strong> The <span data-term="beta">front-back</span>
          and <span data-term="gamma">left-right</span> angles are combined into a single "which way is down"
          vector — the trick that lets one calculation work in any position, and keeps the maths testable.</li>
        <li><strong>Measure the angle.</strong> From that vector Tiltwell derives how far the surface is from
          <span data-term="level">level</span> (a 2-axis <span data-term="spirit level">spirit level</span>),
          or the <span data-term="inclinometer">slope</span> of whatever the phone rests against.</li>
        <li><strong>Convert it.</strong> The angle is shown live as degrees, percent <span data-term="grade">grade</span>,
          <span data-term="roof pitch">roof pitch</span> (rise-in-12) and a 1:n <span data-term="ratio">ratio</span>,
          with a ramp reference for accessibility work.</li>
        <li><strong>Keep the result.</strong> Press Capture to save a labelled reading. You leave with a CSV of
          measurements and a report-card image — all generated on your device. No sensor? The
          <span data-term="protractor">Photo</span> tab measures an angle in any photo.</li>
      </ol>
      <p class="modal-note">Tiltwell is a genuinely useful estimate, not a certified instrument. Phone sensors
        have a small bias; use <span data-term="calibrate">Calibrate</span> to zero it against a reference
        surface when you need relative accuracy.</p>`,
  },
  privacy: {
    title: 'Privacy &amp; threat model',
    body: `
      <h3>Protected</h3>
      <ul>
        <li>The orientation stream is read in the moment and discarded. Nothing is recorded until you press
          <em>Capture</em>, and captures live only in this tab's memory.</li>
        <li>No reading, CSV, report image or photo is ever uploaded. Every calculation and export runs on your
          device. A photo you load for the protractor never leaves the tab.</li>
        <li>No cookies, no fingerprinting, no third-party fonts. The tool works offline once loaded.</li>
      </ul>
      <h3>Not protected</h3>
      <ul>
        <li>Loading the page is an ordinary web request: GitHub Pages (the host) sees your IP and browser, as
          any website does.</li>
        <li>An angle is not personal data on its own, but if you export a CSV, report image or annotated photo
          (PNG or PDF) and then send it somewhere, that is on you — Tiltwell just makes the file.</li>
      </ul>
      <h3>Trust surface</h3>
      <ul>
        <li>The static site bundle, hash-pinned by the GitHub Pages deploy, and the TLS connection to it.</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no
          cross-site tracking; your readings and photos are never sent to it.</li>
        <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open
          the feedback form and press Send; your files and data never are.</li>
      </ul>`,
  },
  about: {
    title: 'About Tiltwell',
    body: `
      <p>Tiltwell turns the <span data-term="orientation sensor">orientation sensor</span> already in your phone
        into a precise 2-axis <span data-term="spirit level">spirit level</span> and an
        <span data-term="inclinometer">inclinometer</span>. Check a shelf is
        <span data-term="level">level</span>, measure a <span data-term="roof pitch">roof pitch</span> for a
        solar quote, or check a <span data-term="ratio">ramp</span> against common accessibility gradients —
        and leave with the number as a CSV and a report card.</p>
      <p>It runs entirely client-side with first-party maths — no server, no account, no upload. Built as part
        of a small workshop of privacy-first browser tools.</p>
      <p class="modal-links">
        <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>
        <a href="https://github.com/ben-gy/tiltwell" target="_blank" rel="noopener">source</a>
      </p>`,
  },
};

let onModalOpen: ((id: string) => void) | null = null;
export function setModalOpenHook(fn: (id: string) => void): void {
  onModalOpen = fn;
}

function wireModals(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-modal]').forEach((btn) => {
    btn.addEventListener('click', () => openModal(root, btn.dataset.modal!));
  });
  root.querySelector('#trust-banner')?.addEventListener('click', () => openModal(root, 'privacy'));
}

export function openModal(root: HTMLElement, id: string): void {
  const def = MODALS[id];
  if (!def) return;
  const host = root.querySelector<HTMLElement>('#modal-host')!;
  const scrim = root.querySelector<HTMLElement>('#scrim')!;
  host.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="${stripTags(def.title)}">
      <div class="modal-head">
        <h2>${def.title}</h2>
        <button type="button" class="modal-close" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body">${def.body}</div>
    </div>`;
  host.hidden = false;
  scrim.hidden = false;
  initGlossary(host);
  const close = () => closeModal(root);
  host.querySelector('.modal-close')?.addEventListener('click', close);
  host.querySelector('.modal')?.addEventListener('click', (e) => e.stopPropagation());
  host.addEventListener('click', close, { once: true });
  onModalOpen?.(id);
}

export function closeModal(root: HTMLElement): void {
  const host = root.querySelector<HTMLElement>('#modal-host')!;
  const scrim = root.querySelector<HTMLElement>('#scrim')!;
  host.hidden = true;
  host.innerHTML = '';
  if (root.querySelector<HTMLElement>('#drawer')!.hidden) scrim.hidden = true;
}

export function isModalOpen(root: HTMLElement): boolean {
  return !root.querySelector<HTMLElement>('#modal-host')!.hidden;
}

function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&');
}

// ── Glossary tooltip ─────────────────────────────────────────────────

function initGlossary(scope: HTMLElement): void {
  scope.querySelectorAll<HTMLElement>('[data-term]').forEach((el) => {
    if (el.dataset.glossReady) return;
    const term = el.dataset.term!;
    if (!GLOSSARY[term]) return;
    el.dataset.glossReady = '1';
    el.classList.add('glossary-link');
    el.tabIndex = 0;
    el.setAttribute('role', 'button');
    const show = (e: Event) => {
      e.stopPropagation();
      showGlossary(el, GLOSSARY[term]);
    };
    el.addEventListener('click', show);
    el.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') show(e);
    });
  });
}

function showGlossary(anchor: HTMLElement, text: string): void {
  const tip = document.querySelector<HTMLElement>('#glossary-tip')!;
  tip.textContent = text;
  tip.hidden = false;
  const r = anchor.getBoundingClientRect();
  const tw = Math.min(320, window.innerWidth - 24);
  tip.style.width = `${tw}px`;
  let left = r.left + r.width / 2 - tw / 2;
  left = Math.max(12, Math.min(left, window.innerWidth - tw - 12));
  tip.style.left = `${left}px`;
  tip.style.top = `${r.bottom + 8}px`;
  const dismiss = (e: Event) => {
    if (e.target !== anchor) {
      tip.hidden = true;
      document.removeEventListener('click', dismiss, true);
    }
  };
  setTimeout(() => document.addEventListener('click', dismiss, true), 0);
}

export function hideGlossary(): void {
  const tip = document.querySelector<HTMLElement>('#glossary-tip');
  if (tip) tip.hidden = true;
}

// ── Toast ────────────────────────────────────────────────────────────

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function showToast(message: string): void {
  const toast = document.querySelector<HTMLElement>('#toast');
  if (!toast) return;
  toast.textContent = message;
  toast.hidden = false;
  toast.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => (toast.hidden = true), 250);
  }, 2600);
}
